import React from 'react';
import Icon from '../../../components/AppIcon';
import { calcularMerma, calcularRendimiento, getRendimientoStatus, RENDIMIENTO_OBJETIVO, RENDIMIENTO_MEDIO_MIN } from '../../../services/rendimientoService';

const RendimientoSection = ({ pesoInicial, pesoAprovechado, onPesoAprovechadoChange, formData, onChange }) => {
  // Support both old and new prop patterns
  const _pesoInicial = pesoInicial ?? formData?.weight ?? '';
  const _pesoAprovechado = pesoAprovechado ?? formData?.pesoAprovechado ?? '';
  const _onPesoAprovechadoChange = onPesoAprovechadoChange ?? ((v) => onChange?.('pesoAprovechado', v));

  const merma = calcularMerma(_pesoInicial, _pesoAprovechado);
  const rendimiento = calcularRendimiento(_pesoInicial, _pesoAprovechado);
  const status = getRendimientoStatus(rendimiento);

  const getRendimientoBarWidth = () => {
    if (rendimiento === null) return 0;
    return Math.min(100, Math.max(0, rendimiento));
  };

  const getBarColor = () => {
    if (!status) return 'rgba(255,255,255,0.1)';
    if (status?.color === 'success') return '#3A7F6B';
    if (status?.color === 'warning') return '#C9A24D';
    return '#E05252';
  };

  const getValueColor = () => {
    if (!status) return 'rgba(255,255,255,0.85)';
    if (status?.color === 'success') return '#3A7F6B';
    if (status?.color === 'warning') return '#C9A24D';
    return '#E05252';
  };

  const inputClass = "w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors";
  const labelClass = "block text-base font-oranienbaum text-white mb-1.5";

  return (
    <div className="space-y-4">
      {/* Input fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Peso inicial: read-only */}
        <div>
          <label className={labelClass}>Peso Inicial (kg)</label>
          <div className="flex items-center gap-2 px-3 py-2.5 bg-[#111111] border border-white/8 rounded-lg">
            <Icon name="Lock" size={12} style={{ color: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
            <span className="text-sm font-oranienbaum text-white/60">
              {_pesoInicial && parseFloat(_pesoInicial) > 0 ? `${parseFloat(_pesoInicial)?.toFixed(2)} kg` : '—'}
            </span>
          </div>
          <p className="text-[10px] font-oranienbaum text-white mt-1">Tomado de Detalles de la Pieza</p>
        </div>

        {/* Peso aprovechado */}
        <div>
          <label className={labelClass}>Peso Aprovechado (kg)</label>
          <input
            type="number"
            className={inputClass}
            placeholder="0.00"
            value={_pesoAprovechado}
            onChange={(e) => _onPesoAprovechadoChange(e?.target?.value)}
            min="0.01"
            max="30"
            step="0.01"
          />
          <p className="text-[10px] font-oranienbaum text-white mt-1">Peso neto tras el loncheado</p>
        </div>
      </div>

      {/* KPI mini-cards: Merma + Rendimiento */}
      <div className="grid grid-cols-2 gap-3">
        {/* Merma */}
        <div className="bg-[#111111] rounded-xl border border-white/8 p-3 flex flex-col justify-between min-h-[80px]">
          <div className="flex items-start justify-between mb-1.5">
            <span className="font-oranienbaum text-white text-[17px]">Merma (kg)</span>
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(224,82,82,0.1)' }}>
              <Icon name="TrendingDown" size={12} style={{ color: '#E05252' }} />
            </div>
          </div>
          <p className="text-lg font-bold font-oranienbaum" style={{ color: merma !== null ? '#E05252' : 'rgba(255,255,255,0.3)' }}>
            {merma !== null ? `${merma?.toFixed(2)} kg` : '—'}
          </p>
          <p className="font-oranienbaum text-white mt-0.5 text-[13px]">Inicial − Aprovechado</p>
        </div>

        {/* Rendimiento */}
        <div className="bg-[#111111] rounded-xl border border-white/8 p-3 flex flex-col justify-between min-h-[80px]"
          style={{ borderColor: rendimiento !== null ? `${getBarColor()}30` : undefined }}>
          <div className="flex items-start justify-between mb-1.5">
            <span className="font-oranienbaum text-white text-[17px]">Rendimiento (%)</span>
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${getBarColor()}18` }}>
              <Icon name="BarChart2" size={12} style={{ color: getBarColor() }} />
            </div>
          </div>
          <p className="text-lg font-bold font-oranienbaum" style={{ color: rendimiento !== null ? getValueColor() : 'rgba(255,255,255,0.3)' }}>
            {rendimiento !== null ? `${rendimiento?.toFixed(1)}%` : '—'}
          </p>
          <p className="font-oranienbaum text-white mt-0.5 text-[13px]">(Aprovechado / Inicial) × 100</p>
        </div>
      </div>

      {/* Visual indicator bar */}
      {rendimiento !== null && (
        <div className="p-3 rounded-xl border" style={{ background: `${getBarColor()}08`, borderColor: `${getBarColor()}20` }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Icon name={status?.icon || 'Minus'} size={13} style={{ color: getBarColor() }} />
              <span className="text-xs font-oranienbaum font-medium" style={{ color: getBarColor() }}>
                {status?.label}
              </span>
            </div>
            <span className="text-xs font-bold font-oranienbaum" style={{ color: getBarColor() }}>
              {rendimiento?.toFixed(1)}%
            </span>
          </div>
          <div className="w-full rounded-full h-1.5" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div
              className="h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${getRendimientoBarWidth()}%`, background: getBarColor() }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[9px] font-oranienbaum text-white/60">0%</span>
            <span className="text-[9px] font-oranienbaum" style={{ color: '#E05252' }}>{RENDIMIENTO_MEDIO_MIN}% mín.</span>
            <span className="text-[9px] font-oranienbaum" style={{ color: '#C9A24D' }}>{RENDIMIENTO_OBJETIVO}% ópt.</span>
            <span className="text-[9px] font-oranienbaum text-white/60">100%</span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#3A7F6B' }} />
          <span className="text-[10px] font-oranienbaum text-white">&gt;{RENDIMIENTO_OBJETIVO}% Superior</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#C9A24D' }} />
          <span className="text-[10px] font-oranienbaum text-white">{RENDIMIENTO_MEDIO_MIN}–{RENDIMIENTO_OBJETIVO}% Óptimo</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#E05252' }} />
          <span className="text-[10px] font-oranienbaum text-white">&lt;{RENDIMIENTO_MEDIO_MIN}% Inferior</span>
        </div>
      </div>
    </div>
  );
};

export default RendimientoSection;
